/**
 * Method select for per-path Traffic distribution AI (offers & landers).
 * Not used for bulk default-path controls — those use `flow-controls/CalculationMethodSelect`.
 */

import type { FC } from 'react';
import { HelpCircle } from 'lucide-react';
import type { VoluumCalculationMethod } from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

import {
  CALCULATION_METHOD_LABEL,
  CALCULATION_METHODS,
} from './path-settings.constants';

const OFFER_LANDER_METHOD_HINT =
  'How Voluum scores landing page and offer weights when Traffic distribution AI is enabled on this path only.';

export interface PathSettingsCalculationMethodSelectProps {
  value: VoluumCalculationMethod;
  onChange: (v: VoluumCalculationMethod) => void;
  disabled?: boolean;
  compact?: boolean;
  className?: string;
}

const SELECT_WIDTH = 'w-[7.25rem] max-w-full';

const PathSettingsCalculationMethodSelect: FC<
  PathSettingsCalculationMethodSelectProps
> = ({
  value,
  onChange,
  disabled = false,
  compact = false,
  className,
}) => (
  <label className={cn('flex flex-col gap-1 self-start', className)}>
    <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
      Method
      <Tooltip content={OFFER_LANDER_METHOD_HINT}>
        <HelpCircle
          className="h-3 w-3"
          aria-label="About offer and lander optimization method"
        />
      </Tooltip>
    </span>
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => {
        const next = e.target.value as VoluumCalculationMethod;
        if (next) onChange(next);
      }}
      className={cn(
        SELECT_WIDTH,
        'rounded-md border border-input bg-background shadow-sm',
        'focus:outline-none focus:ring-2 focus:ring-ring',
        compact ? 'h-7 px-1.5 text-[11px]' : 'h-8 px-2 text-xs',
      )}
      aria-label="Offer and lander weight optimization method"
    >
      {CALCULATION_METHODS.map((m) => (
        <option key={m} value={m}>
          {CALCULATION_METHOD_LABEL[m]}
        </option>
      ))}
    </select>
  </label>
);

export default PathSettingsCalculationMethodSelect;
